import React from 'react';

import {
  HI_TYPES, MULTI_SLOT_TYPES, TYPE_INVOICE, hiTypeLabel,
} from '../../../constants/abdmHiTypes';
import { INVOICE, STRIP } from '../../../constants/hipRecordsCopy';
import { SLOT_STATE, isEmptyRecord, isNotApproved } from '../../../constants/hipRecordEnums';

/**
 * HipRecordTypeStrip - the record-type pill strip on an encounter card (redesign v2, OR-73).
 * Props (FROZEN, R-F4 mounts it; R-F12 / OR-82 added the last four, all optional):
 *   { record, slots, disabled, onAdd(recordType), openType, openPanelId, lockReason, onLockedTap }
 *   record  - the EncounterRow; only `allowedTypes` is read (the server decides which types fit the visit kind,
 *             OR-74: nothing here knows OPD from IPD). An Invoice fits every visit, so it is never "not applicable".
 *   slots   - the slots the card holds (with its applied writes on top), the same list the checklist shows.
 *   disabled - the add buttons (outlined pills and "+") are off; held and greyed pills are unaffected.
 *   onAdd(recordType) - a missing pill or a "+" was clicked: the card opens the file picker for that type, and the
 *             blank invoice form for Invoice.
 *   openType  - the type whose inline Add file panel is open (OR-82): its pill shows as "adding" (aria-expanded +
 *             aria-controls=openPanelId; a held Diagnostic Report gets a ring and its "+" carries the aria).
 *   lockReason - set while a panel is open (one panel per card): every add pill except Invoice is dimmed
 *             (aria-disabled, tooltip = this sentence) and a tap calls onLockedTap() instead of onAdd.
 *
 * ALL 8 types are drawn on EVERY card, always in HI_TYPES order (the M3 order), so a type is always in the same
 * cell: a 5 + 3 grid on a wide screen, 2 columns (4 rows of 2) on a phone. A pill never moves; only its look changes:
 *   not applicable (type not in allowedTypes) - greyed, dotted border, NOT clickable, tooltip says why
 *   missing    - white, outlined, "+": click adds the file
 *   in ArogyaKhata - slate solid, no tick (it is not linked yet), not clickable
 *   linked     - green solid with a tick;  failed - red with "!";  linking / paused / not approved - amber
 * A multi-slot type (Diagnostic Report, Invoice) shows one pill with a count and a "+" that adds another.
 * An OP Consultation that is only the empty record (OR-79, blocked_reason RX_REQUIRED) counts as missing: its pill offers
 * Add file (OR-70), because a staff file is what makes it linkable.
 * The state is also written in words (title + aria-label), never colour alone. Several slots of one type show the
 * most urgent state (failed, paused, not approved, linking), then "all linked", else "in ArogyaKhata".
 * Pure presentation: no state, no fetching. Solid fills in both themes (CLAUDE.md contrast rule).
 */

// The greyed "not for this visit" colours (text, fill, dotted border), 6.9:1 in both themes. Exported: the list legend's
// "greyed" chip wears these same tokens, so the legend can never drift from the pill it explains (OR-88).
export const NOT_APPLICABLE_LOOK = 'border-dotted border-slate-400 bg-slate-100 text-slate-600 dark:border-slate-500 dark:bg-slate-900 dark:text-slate-400';

const PILL = 'inline-flex w-full min-w-0 items-center justify-center gap-1 rounded-full border px-2 py-1.5 text-[11.5px] font-semibold leading-tight md:py-1';
const TONE = {
  inApp: 'border-slate-600 bg-slate-600 text-white dark:border-slate-500 dark:bg-slate-500 dark:text-white',
  linked: 'border-green-700 bg-green-700 text-white dark:border-green-400 dark:bg-green-800 dark:text-green-200',
  wait: 'border-amber-600 bg-yellow-100 text-yellow-800 dark:border-amber-400 dark:bg-yellow-900 dark:text-yellow-200',
  failed: 'border-red-600 bg-red-100 text-red-800 dark:border-red-400 dark:bg-red-900 dark:text-red-300',
  missing: 'border-slate-400 bg-white text-slate-900 enabled:hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-500 dark:bg-slate-800 dark:text-slate-100 dark:enabled:hover:bg-slate-700',
  // The pill whose inline panel is open (OR-82).
  adding: 'border-teal-600 bg-teal-50 font-bold text-teal-800 ring-1 ring-teal-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-teal-400 dark:bg-teal-900 dark:text-teal-200 dark:ring-teal-400',
  notApplicable: `cursor-not-allowed font-medium ${NOT_APPLICABLE_LOOK}`,
};
// The name inside a pill wraps to at most 2 lines ("Health Document" / "Record") instead of a one-line clip, so the full
// M3 name always shows (OR-88). The pill stretches to its cell (Cell is a flex row), so a row of tiles stays one height.
const LABEL = 'min-w-0 line-clamp-2 text-center';
// Another add pill while a panel is open: aria-disabled, still tappable so it can say why.
const LOCKED = 'cursor-not-allowed opacity-60';
// Count badge and "+" sit on a coloured pill: a solid white / slate disc keeps them readable on every tone.
const DISC = 'bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100';
const BADGE = `inline-flex shrink-0 items-center rounded-full px-1.5 text-[10.5px] font-extrabold ${DISC}`;
const PLUS = `inline-grid h-5 w-5 shrink-0 place-items-center rounded-full border border-slate-400 p-0 text-xs font-bold leading-none disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-500 ${DISC}`;

// Held look per state: the tone and the glyph in front of the name (a tick only for linked, "!" for failed).
const LOOK = {
  linked: { tone: TONE.linked, glyph: '✓ ' },
  inApp: { tone: TONE.inApp, glyph: '' },
  linking: { tone: TONE.wait, glyph: '' },
  paused: { tone: TONE.wait, glyph: '' },
  notApproved: { tone: TONE.wait, glyph: '' },
  failed: { tone: TONE.failed, glyph: '! ' },
};

// One state for the slots of one type, most urgent first (keys of STRIP.state).
function heldState(held) {
  if (held.some((s) => s.state === SLOT_STATE.LINK_FAILED)) return 'failed';
  if (held.some((s) => s.state === SLOT_STATE.PAUSED)) return 'paused';
  if (held.some(isNotApproved)) return 'notApproved';
  if (held.some((s) => s.state === SLOT_STATE.LINKING)) return 'linking';
  if (held.every((s) => s.state === SLOT_STATE.LINKED)) return 'linked';
  return 'inApp';
}

// One grid cell = one list item, so the list stays a valid list around the buttons.
function Cell({ type, state, children, ...aria }) {
  return (
    <div role="listitem" data-type={type} data-state={state} className="flex min-w-0" {...aria}>
      {children}
    </div>
  );
}

function TypePill({
  type, applicable, held: slotsOfType, disabled, onAdd, openType, openPanelId, lockReason, onLockedTap,
}) {
  const label = hiTypeLabel(type);

  if (!applicable) {
    const tip = STRIP.notApplicable[type];
    return (
      <Cell type={type} state="notApplicable" aria-disabled="true" title={tip} aria-label={tip}>
        <span data-testid="type-pill" className={`${PILL} ${TONE.notApplicable}`}><span className={LABEL}>{label}</span></span>
      </Cell>
    );
  }

  // The empty OP Consultation record is not "held": nothing can be linked from it yet (OR-79).
  const held = slotsOfType.filter((s) => !isEmptyRecord(s));
  // One panel per card: while one is open every add pill but Invoice is locked (Invoice opens another form, not a panel).
  const adding = openType === type;
  const locked = !!lockReason && type !== TYPE_INVOICE;
  const lockedOut = locked && !adding;
  const add = () => (locked ? onLockedTap() : onAdd(type));

  if (!held.length) {
    let tip = type === TYPE_INVOICE ? INVOICE.card.stripFill(label) : STRIP.addFile(label);
    if (lockedOut) tip = lockReason;
    return (
      <Cell type={type} state="missing">
        <button
          type="button" data-testid="type-pill" className={`${PILL} ${adding ? TONE.adding : TONE.missing} ${lockedOut ? LOCKED : ''}`}
          disabled={disabled} aria-disabled={lockedOut || undefined} aria-expanded={adding || undefined}
          aria-controls={adding ? openPanelId : undefined} title={tip} aria-label={tip} onClick={add}
        >
          <span className={LABEL}>+ {label}</span>
        </button>
      </Cell>
    );
  }

  const state = heldState(held);
  const word = STRIP.state[state];
  const multi = MULTI_SLOT_TYPES.includes(type);
  const addAnother = lockedOut ? lockReason : STRIP.addAnother(label);
  return (
    <Cell type={type} state={state} title={word} aria-label={STRIP.pillLabel(label, word, held.length)}>
      <span data-testid="type-pill" className={`${PILL} ${LOOK[state].tone} ${adding ? 'ring-2 ring-teal-500 dark:ring-teal-400' : ''}`}>
        <span className={LABEL}>{LOOK[state].glyph}{label}</span>
        {multi && held.length > 1 && <span className={BADGE}>{held.length}</span>}
        {multi && (
          <button
            type="button" className={`${PLUS} ${lockedOut ? LOCKED : ''}`} disabled={disabled} aria-disabled={lockedOut || undefined}
            aria-expanded={adding || undefined} aria-controls={adding ? openPanelId : undefined}
            title={addAnother} aria-label={addAnother} onClick={add}
          >
            +
          </button>
        )}
      </span>
    </Cell>
  );
}

export default function HipRecordTypeStrip({
  record, slots, disabled, onAdd, openType, openPanelId, lockReason, onLockedTap,
}) {
  return (
    <div
      role="list" aria-label={STRIP.listLabel}
      className="grid grid-cols-2 gap-1.5 md:grid-cols-[repeat(5,minmax(0,176px))] md:justify-start"
    >
      {HI_TYPES.map((type) => (
        <TypePill
          key={type} type={type} disabled={disabled} onAdd={onAdd}
          openType={openType} openPanelId={openPanelId} lockReason={lockReason} onLockedTap={onLockedTap}
          applicable={type === TYPE_INVOICE || record.allowedTypes.includes(type)}
          held={slots.filter((s) => s.recordType === type)}
        />
      ))}
    </div>
  );
}
